/**
 * Admin Store — 管理後台狀態管理
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as AdminAPI from '../services/AdminAPI';

interface AdminStore {
  // State
  cards: AdminAPI.CardDefinition[];
  cardsTotalCount: number;
  cardsOffset: number;
  cardsSearch: string;

  packs: AdminAPI.CardPack[];

  players: AdminAPI.PaginatedPlayers['players'];
  playersTotalCount: number;
  playersOffset: number;
  playersSearch: string;

  selectedPlayer: AdminAPI.AdminPlayerDetail | null;
  stats: AdminAPI.AdminStats | null;

  isLoading: boolean;
  error: string | null;

  // Actions
  loadCards: (offset?: number, search?: string) => Promise<void>;
  createCard: (card: Partial<AdminAPI.CardDefinition>) => Promise<void>;
  updateCard: (code: string, card: Partial<AdminAPI.CardDefinition>) => Promise<void>;
  deleteCard: (code: string) => Promise<void>;
  importCards: (cards: unknown[]) => Promise<{ imported: number; total: number }>;
  importFromSheet: (sheetId: string, range?: string) => Promise<{ imported: number; total: number }>;

  loadPacks: () => Promise<void>;
  createPack: (pack: Parameters<typeof AdminAPI.createPack>[0]) => Promise<void>;
  updatePack: (id: string, pack: Partial<AdminAPI.CardPack>) => Promise<void>;
  deletePack: (id: string) => Promise<void>;

  loadPlayers: (offset?: number, search?: string) => Promise<void>;
  loadPlayerDetail: (id: string) => Promise<void>;

  loadStats: () => Promise<void>;

  clearError: () => void;
}

export const useAdminStore = create<AdminStore>()(
  devtools(
    (set, get) => ({
      cards: [],
      cardsTotalCount: 0,
      cardsOffset: 0,
      cardsSearch: '',
      packs: [],
      players: [],
      playersTotalCount: 0,
      playersOffset: 0,
      playersSearch: '',
      selectedPlayer: null,
      stats: null,
      isLoading: false,
      error: null,

      loadCards: async (offset?: number, search?: string) => {
        const o = offset ?? get().cardsOffset;
        const s = search ?? get().cardsSearch;
        set({ isLoading: true, error: null });
        try {
          const result = await AdminAPI.listCards(o, 50, s);
          set({
            cards: result.cards,
            cardsTotalCount: result.total_count,
            cardsOffset: o,
            cardsSearch: s,
            isLoading: false,
          });
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      createCard: async (card) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.createCard(card);
          await get().loadCards();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      updateCard: async (code, card) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.updateCard(code, card);
          await get().loadCards();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      deleteCard: async (code) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.deleteCard(code);
          await get().loadCards();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      importCards: async (cards) => {
        set({ isLoading: true, error: null });
        try {
          const result = await AdminAPI.importCards(cards);
          await get().loadCards();
          return result;
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
          throw err;
        }
      },

      importFromSheet: async (sheetId, range) => {
        set({ isLoading: true, error: null });
        try {
          const result = await AdminAPI.importFromSheet(sheetId, range);
          await get().loadCards();
          return result;
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
          throw err;
        }
      },

      loadPacks: async () => {
        set({ isLoading: true, error: null });
        try {
          const packs = await AdminAPI.listPacks();
          set({ packs, isLoading: false });
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      createPack: async (pack) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.createPack(pack);
          await get().loadPacks();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      updatePack: async (id, pack) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.updatePack(id, pack);
          await get().loadPacks();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      deletePack: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await AdminAPI.deletePack(id);
          await get().loadPacks();
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      loadPlayers: async (offset?: number, search?: string) => {
        const o = offset ?? get().playersOffset;
        const s = search ?? get().playersSearch;
        set({ isLoading: true, error: null });
        try {
          const result = await AdminAPI.listPlayers(o, 50, s);
          set({
            players: result.players,
            playersTotalCount: result.total_count,
            playersOffset: o,
            playersSearch: s,
            isLoading: false,
          });
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      loadPlayerDetail: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const detail = await AdminAPI.getPlayerDetail(id);
          set({ selectedPlayer: detail, isLoading: false });
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      loadStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const stats = await AdminAPI.getStats();
          set({ stats, isLoading: false });
        } catch (err) {
          set({ isLoading: false, error: (err as Error).message });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'AdminStore' },
  ),
);
